import {
  NEW_TUTOR_ONBOARDING_TOPIC,
  SAT_BOOTCAMP_ONBOARDING_TOPIC,
  SessionAttendeeRole,
  type VolunteerActivityType,
} from '@/queries/constants'
import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import TrainingQ from '@/queries/TrainingQ'
import TrainingSubmissionQ from '@/queries/TrainingSubmissionQ'
import TutorAuditQ from '@/queries/TutorAuditQ'
import UserQ from '@/queries/UserQ'
import VolunteerActivityQ from '@/queries/VolunteerActivityQ'
import type { UserContextType } from '@/client/context/user'
import { type Query, SQL } from '@/client/query'

import type { RelatedEntity, VolunteerActivityComponentAttributes } from './types'

// Sometimes, volunteer activities can be connected to another entity. For example, 'host-session'
// or 'host-session-prep' volunteer activities have a related Session that they are attached to.
// This helper fetches all required information to determine and construct the entity related
// to the volunteer activity (everything must be fetched upfront, due to nature of query builder).
function getRelatedEntity(activity: Query, currentUser: UserContextType): RelatedEntity {
  const hostSession = activity.one(
    SessionHostQ.where(
      {
        include: [
          {
            association: 'Session',
            required: true,
            include: [
              {
                association: 'Topic',
                required: true,
              },
            ],
          },
        ],
      },
      SQL`${SessionHostQ.session_host_volunteer_activity_id} = ${VolunteerActivityQ.id}`
    )
  )
  const hostSessionId = hostSession(SessionQ.id)
  const hostSessionSeriesId = hostSession(SessionQ.series_id)
  const hostSessionTopicName = hostSession(TopicQ.name)

  const sessionPrep = activity.one(
    SessionHostQ.where(
      {
        include: [
          {
            association: 'Session',
            required: true,
            include: [
              {
                association: 'Topic',
                required: true,
              },
            ],
          },
        ],
      },
      SQL`${SessionHostQ.session_prep_volunteer_activity_id} = ${VolunteerActivityQ.id}`
    )
  )
  const sessionPrepId = sessionPrep(SessionQ.id)
  const sessionPrepSeriesId = sessionPrep(SessionQ.series_id)
  const sessionPrepTopicName = sessionPrep(TopicQ.name)

  const tutorTrainingSubmission = activity.one(
    TrainingSubmissionQ.where(
      {
        include: [
          {
            association: 'Training',
            required: true,
          },
        ],
      },
      SQL`${TrainingSubmissionQ.volunteer_activity_id} = ${VolunteerActivityQ.id}`
    )
  )
  const tutorTrainingSubmissionId = tutorTrainingSubmission(TrainingSubmissionQ.id)
  const tutorTrainingSlug = tutorTrainingSubmission(TrainingQ.slug)

  const tutorAudit = activity.one(
    TutorAuditQ.where({}, SQL`${TutorAuditQ.volunteer_activity_id} = ${VolunteerActivityQ.id}`)
  )
  const tutorAuditId = tutorAudit(TutorAuditQ.id)

  // Have to alias TrainingSubmission so there's no conflict with parent query.
  const hasCurrentUserCompletedTraining =
    tutorTrainingSubmission<boolean>`
          COUNT(*) > 0
          FROM ${TrainingSubmissionQ} t2
          WHERE t2.user_id = ${currentUser.id} AND
          t2.training_id = ${TrainingSubmissionQ.training_id} AND
          t2.submitted_at IS NOT NULL
          ` ?? false

  const onboardingBuddySessionAttendee = activity.one(
    SessionAttendeeQ.where(
      { roles: [SessionAttendeeRole.OnboardingBuddy] },
      SQL`${SessionAttendeeQ.volunteer_activity_id} = ${VolunteerActivityQ.id}`
    )
  )
  const onboardingBuddySessionAttendeeId = onboardingBuddySessionAttendee(SessionAttendeeQ.id)
  const onboardingBuddySessionAttendeeSessionId = onboardingBuddySessionAttendee(
    SessionAttendeeQ.session_id
  )

  const newTutorOnboardingSessionAttendee = activity.one(
    SessionAttendeeQ.where(
      {
        include: [
          {
            association: 'Session',
            required: true,
            include: [
              {
                association: 'Topic',
                required: true,
                where: SQL`${TopicQ.name} = ${NEW_TUTOR_ONBOARDING_TOPIC}`,
              },
            ],
          },
        ],
        roles: [SessionAttendeeRole.Learner],
      },
      SQL`${SessionAttendeeQ.volunteer_activity_id} = ${VolunteerActivityQ.id}`
    )
  )
  const newTutorOnboardingSessionAttendeeId = newTutorOnboardingSessionAttendee(SessionAttendeeQ.id)
  const newTutorOnboardingSessionAttendeeSessionId = newTutorOnboardingSessionAttendee(
    SessionAttendeeQ.session_id
  )

  const satBootcampOnboardingSessionAttendee = activity.one(
    SessionAttendeeQ.where(
      {
        include: [
          {
            association: 'Session',
            required: true,
            include: [
              {
                association: 'Topic',
                required: true,
                where: SQL`${TopicQ.name} = ${SAT_BOOTCAMP_ONBOARDING_TOPIC}`,
              },
            ],
          },
        ],
        roles: [SessionAttendeeRole.Learner],
      },
      SQL`${SessionAttendeeQ.volunteer_activity_id} = ${VolunteerActivityQ.id}`
    )
  )
  const satBootcampOnboardingSessionAttendeeId = satBootcampOnboardingSessionAttendee(
    SessionAttendeeQ.id
  )
  const satBootcampOnboardingSessionAttendeeSessionId = satBootcampOnboardingSessionAttendee(
    SessionAttendeeQ.session_id
  )

  if (hostSessionId != null) {
    return {
      type: 'host-session',
      id: hostSessionId,
      isUserTutor: true,
      seriesId: hostSessionSeriesId,
      topicName: hostSessionTopicName ?? '',
    }
  }

  if (sessionPrepId != null) {
    return {
      type: 'host-session-prep',
      id: sessionPrepId,
      isUserTutor: true,
      seriesId: sessionPrepSeriesId,
      topicName: sessionPrepTopicName ?? '',
    }
  }

  if (onboardingBuddySessionAttendeeId != null) {
    return {
      type: 'onboarding-buddy',
      id: onboardingBuddySessionAttendeeId,
      sessionId: onboardingBuddySessionAttendeeSessionId ?? 0,
    }
  }

  if (newTutorOnboardingSessionAttendeeId != null) {
    return {
      type: 'attend-new-tutor-onboarding-session',
      id: newTutorOnboardingSessionAttendeeId,
      sessionId: newTutorOnboardingSessionAttendeeSessionId ?? 0,
    }
  }

  if (satBootcampOnboardingSessionAttendeeId != null) {
    return {
      type: 'attend-sat-bootcamp-onboarding-session',
      id: satBootcampOnboardingSessionAttendeeId,
      sessionId: satBootcampOnboardingSessionAttendeeSessionId ?? 0,
    }
  }

  if (tutorTrainingSubmissionId != null) {
    return {
      type: 'tutor-training',
      id: tutorTrainingSubmissionId,
      slug: `${tutorTrainingSlug}`,
      hasCurrentUserCompletedTraining,
    }
  }

  if (tutorAuditId != null) {
    return {
      type: 'auditor',
      id: tutorAuditId,
    }
  }

  return null
}

export default function transformVolunteerActivityQuery(
  currentUser: UserContextType,
  activity: Query
): VolunteerActivityComponentAttributes {
  const approvedAt = activity(VolunteerActivityQ.approved_at)
  const date = activity(VolunteerActivityQ.date)
  const requestedChangesAt = activity(VolunteerActivityQ.requested_changes_at)

  const reviewer = activity.one(VolunteerActivityQ.getReviewer())

  const relatedEntity = getRelatedEntity(activity, currentUser)

  return {
    query: activity,
    id: activity(VolunteerActivityQ.id) ?? 0,
    user_id: activity(VolunteerActivityQ.user_id) ?? 0,
    type: `${activity(VolunteerActivityQ.type)}` as VolunteerActivityType,
    date: date == null ? new Date() : new Date(`${date}`),
    duration_seconds: activity(VolunteerActivityQ.duration_seconds) ?? 0,
    description: `${activity(VolunteerActivityQ.description)}`,
    links: activity(VolunteerActivityQ.links),
    requested_changes: activity(VolunteerActivityQ.requested_changes),
    requested_changes_at: requestedChangesAt == null ? null : new Date(requestedChangesAt),
    last_submitted_at: new Date(`${activity(VolunteerActivityQ.last_submitted_at)}`),
    created_at: new Date(`${activity(VolunteerActivityQ.created_at)}`),
    approved_at: approvedAt == null ? null : new Date(approvedAt),
    related_entity: relatedEntity,
    reviewer: {
      id: activity(VolunteerActivityQ.reviewer_id),
      displayName: reviewer(UserQ.display_name),
      username: reviewer(UserQ.username),
    },
  }
}
